import React from 'react';
import { Spinner, Toaster } from '@fluentui/react-components';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
} from '@fluentui/react-components';
import { SearchBox } from '@fluentui/react';
import { InstructionsContainer, H1 } from './Pages-styles';
import { usePageContext } from '../hooks/PageContext';
import { Note } from '../graphql/types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArticleCard,
  BlogStyles,
  NewArticleButton,
  SelectedNotesBar,
  BlogPagination,
  BackButton,
  BlogEdit,
  BlogView,
} from './BlogContent';
import { SEOHead } from '../components/SEO/SEOHead';

export const Blog: React.FC = () => {
  const { actions } = usePageContext();
  const { noteId } = useParams<{ noteId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); 

  const {
    notes,
    loading,
    mode,
    currentNote,
    setMode,
    setCurrentNote,
    search,
    tagFilter,
    setSearch,
    setTagFilter,
    confirmDeleteMultiple,
    setConfirmDeleteMultiple,
    selectedNotes,
    handleDeleteSelected,
  } = actions;

    // Сбрасываем режим при изменении URL параметров
  React.useEffect(() => {
    const tagFromUrl = searchParams.get('tag');
    
    if (tagFromUrl) {
      // Если есть тег в URL - показываем список с фильтром
      setTagFilter(tagFromUrl);
      setSearch('');
      setMode('list'); // ← ВАЖНО: Принудительно сбрасываем режим
      setCurrentNote(null); // ← ВАЖНО: Закрываем открытую статью
    } else if (!noteId) {
      // Если нет ни тега, ни ID заметки - показываем полный список
      setTagFilter(null);
      setSearch('');
      setMode('list');
      setCurrentNote(null);
    }
  }, [searchParams, noteId, setMode, setCurrentNote, setTagFilter, setSearch]);

    // Обработка перехода к конкретной заметке по ID
  React.useEffect(() => {
    if (noteId) {
      const note = notes.find((n: { id: string }) => n.id === noteId);
      if (note) {
        setCurrentNote(note);
        setMode('view');
      } else if (!loading) {
        navigate('/blog', { replace: true });
      }
    }
  }, [noteId, notes, loading, setCurrentNote, setMode, navigate]);

  // ✅ Читаем тег из URL и применяем фильтр
  React.useEffect(() => {
    const tagFromUrl = searchParams.get('tag');
    if (tagFromUrl) {
      setTagFilter(tagFromUrl);
      setSearch(''); // Сбрасываем поиск при фильтрации по тегу
    }
  }, [searchParams, setTagFilter, setSearch]);

  // Режим редактирования
  if (mode === 'edit') return <><BlogStyles /><BlogEdit /></>;

  // Режим просмотра
  if (mode === 'view' && currentNote) return <><BlogStyles /><BlogView /></>;

  // Режим списка
  return (
    <>
      <BlogStyles />
          <SEOHead
            title="СметаFORTE - Главная"
            description="СметаFORTE - профессиональный модуль для сметного дела. Сопоставительные ведомости, управление проектами, автоматизация расчетов."
            keywords={['смета', 'СВОР', 'Ведомость', 'ведомость', 'свор', 'работы', 'сметы', 'сопоставительная ведомость объёмов работ', 'цэс', 'свор быстро', 'автоматически', 'объёмы работ', 'программый модуль', '', '', '', 'строительство', 'автоматизация', 'ГРАНД-Смета', 'СметаFORTE']}
          />
      <Toaster toasterId="blog-toast" />

      {/* ✅ Диалог удаления нескольких заметок */}
      <Dialog
        open={confirmDeleteMultiple}
        onOpenChange={(_: any, data: any) => {
          if (!data.open) setConfirmDeleteMultiple(false);
        }}
        modalType="modal"
      >
        <DialogSurface
          style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          <DialogBody>
            <DialogTitle>Удалить статьи?</DialogTitle>
            <DialogContent>
              Вы уверены, что хотите удалить {selectedNotes.size} статей? Это действие нельзя отменить.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmDeleteMultiple(false)}>
                Отмена
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  handleDeleteSelected();
                  setConfirmDeleteMultiple(false);
                }}
              >
                Удалить
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <InstructionsContainer>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          {tagFilter && (
            <BackButton
              title="Назад к полному списку"
              style={{ marginRight: '10px' }}
              onClick={() => {
                setTagFilter(null);
                setSearch('');
              }}
            />
          )}
          <H1>{tagFilter ? `Статьи по тегу: #${tagFilter}` : 'Статьи'}</H1>
        </div>

        <SearchBox
          placeholder="Поиск..."
          value={search}
          onChange={(_, newValue) => setSearch(newValue || '')}
          styles={{ root: { marginBottom: 20, width: '100%' } }}
        />

        <SelectedNotesBar />
        <NewArticleButton />

        <div className="blog-container-wrapper">
          {loading ? (
            <Spinner label="Загрузка..." />
          ) : (
            <div className="blog-grid">
              {notes.map((note: Note) => (
                <ArticleCard key={note.id} note={note} />
              ))}
            </div>
          )}
        </div>
      </InstructionsContainer>

      {/* ✅ Пагинация — только кнопка "Показать ещё" */}
      <BlogPagination />
    </>
  );
};

export default Blog;